import React, { useEffect, useState, useRef, useContext } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { NullStream } from "pdfjs-dist/build/pdf.worker";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import BackToDashboard from "../../components/BackToDashboard";
import { useTranslation } from "react-i18next";

const MessagesViewer = () => {
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState("inbound");
  const [totalPages, setTotalPages] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const messagesEndRef = useRef(null);

  const LIMIT = 10;

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL}/allMessages`, {
        params: {
          page: page,
          limit: LIMIT,
          direction,
        },
        headers: {
          Authorization: `Bearer ${userData?.token}`,
        },
      });
      setMessages(res.data.messages || []);

      if (res.data.totalPages) {
        setTotalPages(res.data.totalPages);
      } else if (res.data.total) {
        setTotalPages(Math.ceil(res.data.total / LIMIT));
      } else {
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [page, direction]);

  const handleDirectionChange = (dir) => {
    setDirection(dir);
    setPage(1);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchMessages();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToTop();
  }, [page]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.4,
        ease: "easeOut",
      },
    }),
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  const buttonVariants = {
    hover: { scale: 1.05, boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)" },
    tap: { scale: 0.95 },
  };

  const pulseAnimation = {
    scale: [1, 1.05, 1],
    transition: { duration: 1.5, repeat: Infinity, repeatType: "reverse" },
  };

  const authorized = true;

  return (
    <PageWrapper>
      {authorized ? (
        <motion.div
          className="messages-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >

          <motion.div
            className="header-section"
            initial={{ y: -50 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
          >
            <h1 className="messages-title">
              <motion.span
                initial={{ rotateZ: 0 }}
                animate={{ rotateZ: [0, -10, 0] }}
                transition={{ duration: 1.5, delay: 1 }}
                className="title-emoji"
              >
                📬
              </motion.span>
              <span className="title-text">WhatsApp Messages</span>
            </h1>

            <motion.div
              className="refresh-button"
              whileHover={buttonVariants.hover}
              whileTap={buttonVariants.tap}
              onClick={handleRefresh}
              animate={isRefreshing ? { rotate: 360 } : {}}
              transition={isRefreshing ? { duration: 0.6 } : {}}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </motion.div>
          </motion.div>

          <motion.div
            className="tabs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <motion.button
              className={`tab-button ${
                direction === "inbound" ? "active" : ""
              }`}
              onClick={() => handleDirectionChange("inbound")}
              whileHover={buttonVariants.hover}
              whileTap={buttonVariants.tap}
              animate={direction === "inbound" ? { scale: [1, 1.05, 1] } : {}}
              transition={direction === "inbound" ? { duration: 0.3 } : {}}
            >
              <motion.span
                animate={direction === "inbound" ? pulseAnimation : {}}
              >
                🟢 Inbound
              </motion.span>
            </motion.button>

            <motion.button
              className={`tab-button ${
                direction === "outbound-api" ? "active" : ""
              }`}
              onClick={() => handleDirectionChange("outbound-api")}
              whileHover={buttonVariants.hover}
              whileTap={buttonVariants.tap}
              animate={
                direction === "outbound-api" ? { scale: [1, 1.05, 1] } : {}
              }
              transition={direction === "outbound-api" ? { duration: 0.3 } : {}}
            >
              <motion.span
                animate={direction === "outbound-api" ? pulseAnimation : {}}
              >
                🔵 Outbound
              </motion.span>
            </motion.button>
          </motion.div>

          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loader"
                className="loader"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="loader-spinner"
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray="15 30"
                    />
                  </svg>
                </motion.div>
                <p>Loading messages...</p>
              </motion.div>
            ) : (
              <motion.div
                key="content"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0 }}
              >
                <AnimatePresence>
                  <motion.div className="messages-grid">
                    {messages.length === 0 ? (
                      <motion.div
                        className="empty-state"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <svg
                          width="60"
                          height="60"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#ccc"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="3"
                            y="5"
                            width="18"
                            height="14"
                            rx="2"
                            ry="2"
                          ></rect>
                          <polyline points="3 7 12 13 21 7"></polyline>
                        </svg>
                        <h3 className="empty-state-title">No messages found</h3>
                        <p className="empty-state-message">
                          Try selecting a different direction or refreshing.
                        </p>
                      </motion.div>
                    ) : (
                      messages.map((msg, index) => (
                        <motion.div
                          key={msg.sid || index}
                          className="message-card"
                          custom={index}
                          variants={messageVariants}
                          whileHover={{
                            y: -8,
                            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.12)",
                            transition: { duration: 0.2 },
                          }}
                          data-direction={msg.direction}
                        >
                          <div className="message-header">
                            <div className="message-date">
                              {new Date(msg.dateSent).toLocaleString()}
                            </div>
                            <div
                              className={`message-status ${msg.direction}`}
                            ></div>
                          </div>

                          <div className="message-info">
                            <div className="info-row">
                              <span className="info-label">Direction:</span>
                              <span className={`direction ${msg.direction}`}>
                                {msg.direction}
                              </span>
                            </div>
                            <div className="info-row">
                              <span className="info-label">From:</span>
                              <span className="from">{msg.from}</span>
                            </div>
                            <div className="info-row">
                              <span className="info-label">To:</span>
                              <span className="to">{msg.to}</span>
                            </div>
                          </div>

                          <div className="message-body-container">
                            <p className="message-body">{msg.body}</p>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </motion.div>
                </AnimatePresence>

                {messages.length > 0 && (
                  <motion.div
                    className="pagination-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <div className="pagination-buttons">
                      <motion.button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        whileHover={
                          !loading && page !== 1 ? buttonVariants.hover : {}
                        }
                        whileTap={
                          !loading && page !== 1 ? buttonVariants.tap : {}
                        }
                        className="pagination-button prev"
                      >
                        ← Previous
                      </motion.button>

                      <div className="pagination-indicator">
                        <div className="page-dots">
                          {Array.from(
                            { length: totalPages <= 5 ? totalPages : 5 },
                            (_, i) => {
                              let pageNum = i + 1;
                              if (totalPages > 5) {
                                if (page <= 3) {
                                  pageNum = i + 1;
                                } else if (page >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = page - 2 + i;
                                }
                              }

                              return (
                                <motion.button
                                  key={i}
                                  className={`page-dot ${
                                    pageNum === page ? "active" : ""
                                  }`}
                                  onClick={() => setPage(pageNum)}
                                  whileHover={{ scale: 1.2 }}
                                  whileTap={{ scale: 0.9 }}
                                  animate={
                                    pageNum === page
                                      ? { scale: [1, 1.2, 1] }
                                      : {}
                                  }
                                  transition={
                                    pageNum === page ? { duration: 0.5 } : {}
                                  }
                                >
                                  {pageNum}
                                </motion.button>
                              );
                            }
                          )}
                        </div>
                      </div>

                      <motion.button
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages}
                        whileHover={
                          !loading && page !== totalPages
                            ? buttonVariants.hover
                            : {}
                        }
                        whileTap={
                          !loading && page !== totalPages
                            ? buttonVariants.tap
                            : {}
                        }
                        className="pagination-button next"
                      >
                        Next →
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </motion.div>
      ) : (
        <p>NOT AUTHORIZED</p>
      )}
    </PageWrapper>
  );
};

export default MessagesViewer;
